import { AdminMessage } from "@components/admin/admin-helpers"
import { SessionTicketWidget } from "@components/film/SessionTicketWidget"
import { EventStatus, EventType } from "@generated-types"
import { ContentBlock, ContentBlocks, defineBlock, EditableText } from "eddev/blocks"
import { useRoute, useRouter } from "eddev/routing"
import { animate } from "motion/react"
import { useEffect, useRef } from "react"

export const meta: BlockMeta = {
  title: "Film Tickets",
  description: "Film Tickets",
  category: "Film",
  tags: ["film-text"],
  icon: "tickets",
}

export default defineBlock("film/film-tickets", (props) => {
  const ref = useRef<HTMLDivElement>(null)
  let upsell = props.film?.tffCenter
    ? props.sffSettings?.tffSettings?.defaultTffUpsellPattern?.contentBlocks
    : props.sffSettings?.festivalSettings?.defaultUpsellPattern?.contentBlocks
  const sessions = props.hideTickets ? [] : props.film?.sessions?.filter(Boolean)

  if (props.customUpsellPattern) {
    upsell = props.customUpsellPattern.contentBlocks
  }

  if (props.hideUpsell) {
    upsell = undefined
  }

  let shouldShow = !!sessions?.length || !!upsell?.length
  if (props.film?.eventInfo?.eventType === EventType.Short) {
    shouldShow = false
  }

  // Scroll to tickets if #tickets is present
  const route = useRoute()
  const router = useRouter()
  useEffect(() => {
    const match = route.hash.match(/#tickets\/?([0-9]+)?/)
    if (match) {
      // Scroll down
      setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth" }), 300)

      // If there's a Ferve ID, bounce the element
      const ferveId = match[1]
      if (ferveId) {
        const element = document.querySelector(`[data-session-ferve-id="${ferveId}"]`) as HTMLElement
        if (element) {
          animate([
            [element, { scale: 1.04 }, { type: "spring", stiffness: 300, damping: 40, delay: 1.1 }],
            [element, { scale: 1 }, { type: "spring", stiffness: 300, damping: 20, delay: 0.1 }],
          ])
        }
      }
      router.replaceHash("")
    }
  }, [route.hash])

  if (!shouldShow) {
    if (env.admin) {
      return <AdminMessage className="my-4">No tickets or upsells to show</AdminMessage>
    }
    return null
  }

  return (
    <div className="group/tickets flex flex-col gap-5 my-10" ref={ref}>
      <EditableText
        store="heading"
        as="h2"
        defaultValue="Tickets"
        placeholder="Tickets"
        className="type-label-caps text-grey"
      />
      <div className="flex flex-col gap-4">
        {sessions?.map((session, i) => (
          <div key={i} data-session-ferve-id={session?.info?.ferveId}>
            <SessionTicketWidget session={session!} />
          </div>
        ))}
        <div data-mixpanel-area="Ticketing Widget Upsell">{upsell && <ContentBlocks blocks={upsell} />}</div>
        <div className="type-body-s italic text-balance hidden group-has-[[data-book-button]:enabled]/tickets:block opacity-80">
          <EditableText
            className="basic-text-styles"
            store="accessibilityText"
            as="div"
            defaultValue={props.sffSettings?.festivalSettings?.standardAccessibilityText ?? ""}
          />
        </div>
      </div>
    </div>
  )
})
